/* Universal Importer & Grouping Engine
   - Normalizes CSV rows (header:true) into executions
   - Classifies asset class for filtering
  - Builds trades using position-based open/close/reversal logic
   - Produces aggregated Trade records per user's spec
*/

import { getContractMultiplier, getForexQuoteCurrency } from '../../constants/contractMultipliers';
import { resolveIndexInstrument } from '../../constants/indexInstruments';
import { formatInTimeZone } from 'date-fns-tz';
import { normalizeTimestampToMs } from '../time/normalizeTimestampToMs';
import { resolveSourceTimestamp } from '../time/sourceTimestamp';
import { getSessionInfo, type AssetClass as SessionAssetClass } from '../market/sessionValidator';
import { parseFlexibleNumber, parseForexQuantityValue } from '../forex/quantity';
import { calculateFifoGrossPnl } from '../trades/fifoPnl';

type RawRow = Record<string, any>;

type ExecutionScope = {
  tenantId?: string;
  userId?: string;
  accountId?: string;
  sourceAccountRef?: string;
  sourceBroker?: string;
};

export type Execution = ExecutionScope & {
  id: string;
  timestamp: number; // ms epoch
  symbol: string;
  side: 'buy' | 'sell';
  qty: number;
  price: number;
  commission: number;
  brokerReportedPnl?: number;
  orderId?: string;
  notes?: string;
  tags?: string[];
  assetClass?: 'STOCKS' | 'FUTURES' | 'CASH' | 'CRYPTO' | 'UNKNOWN';
  // Honest labeling for executions derived from a completed-trade summary row.
  // These are canonical proxy records (entry/exit), not original broker fills.
  executionSource?: string;
  isSyntheticExecution?: boolean;
  // Optional metadata populated only by the completed-trade summary adapter.
  // These carry the source file's own values verbatim (never recalculated).
  strategy?: string;
  setup?: string;
  marketSession?: string;
  timeframe?: string;
  riskAmount?: number;
  initialStop?: number;
  targetPrice?: number;
  mae?: number;
  mfe?: number;
  grossPnl?: number;
  fees?: number;
  sourceBroker?: string;
  sourceStatus?: string;
  shared?: string;
  account?: string;
  instrument?: string;
  instrumentType?: string;
  canonicalSymbol?: string;
  indexName?: string;
  sourceExecutionCount?: number;
  metadata?: {
    sourceExecutionId?: string;
    orderingEvidence?: {
      sourceRowOrdinal?: number;
      timestampOrderKeyNs?: string | null;
      brokerSequence?: string | null;
      brokerSequenceScope?: string | null;
      sequenceEvidenceSource?: string | null;
      sequenceVerified?: boolean;
      timestampPrecision?: Record<string, unknown>;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
};

export type AggregatedTrade = ExecutionScope & {
  id: number;
  tradeId: string;
  sourceOrderIds: string[];
  isReversal: boolean;
  reversalOf?: string;
  reversalChainId?: string;
  symbol: string;
  side: 'Long' | 'Short';
  openDate: string;
  closeDate?: string | null;
  openTimestampMs: number;
  closeTimestampMs?: number | null;
  duration?: string;
  entryPrice?: number;
  exitPrice?: number;
  volume: number; // total traded (sum of qty)
  quantity: number; // max size
  status?: 'open' | 'closed';
  pnl?: number | null; // backward-compatible alias of calculatedPnl
  calculatedPnl?: number | null;
  brokerReportedPnl?: number | null;
  pnlDifference?: number | null; // brokerReportedPnl - calculatedPnl
  commission: number;
  executions: Execution[];
  notes?: string;
  tags?: string[];
  assetClass?: 'STOCKS' | 'FUTURES' | 'CASH' | 'CRYPTO' | 'UNKNOWN';
  // For CASH (forex) trades, pnlCurrency indicates the currency of calculatedPnl (quote currency)
  pnlCurrency?: string;
  // Optional metadata populated only by the completed-trade summary adapter.
  // These carry the source file's own values verbatim (never recalculated).
  strategy?: string;
  setup?: string;
  marketSession?: string;
  timeframe?: string;
  riskAmount?: number;
  initialStop?: number;
  targetPrice?: number;
  mae?: number;
  mfe?: number;
  grossPnl?: number;
  fees?: number;
  sourceBroker?: string;
  sourceStatus?: string;
  shared?: string;
  account?: string;
  instrument?: string;
  instrumentType?: string;
  canonicalSymbol?: string;
  indexName?: string;
  sourceExecutionCount?: number;
};

function hashStringToPositiveInt(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) h = (h * 31 + input.charCodeAt(i)) | 0;
  return Math.abs(h || 1);
}

function parseNumber(v: unknown): number {
  const n = parseFlexibleNumber(v);
  return n == null ? 0 : n;
}

function parseOptionalNumber(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = parseFlexibleNumber(v);
  return n == null ? undefined : n;
}

function hasExplicitTimeZone(value: string): boolean {
  return /(?:z|[+-]\d{2}:?\d{2})$/i.test(value) || /\b(?:utc|gmt)\b/i.test(value)
    || /(?:\[[A-Za-z_]+\/[A-Za-z0-9_+\-/]+\]|\s[A-Za-z_]+\/[A-Za-z0-9_+\-/]+)$/.test(value);
}

function parseDateToTimestampMs(v: unknown, timeZone?: string): number | undefined {
  return resolveSourceTimestamp(v, timeZone).timestampMs;
}

function getTimestampFailureReason(value: unknown, timeZone?: string): string {
  const error = resolveSourceTimestamp(value, timeZone).error;
  if (error === 'timezone_required') return 'source timezone confirmation required';
  if (error === 'invalid_timezone') return `invalid source timezone: ${timeZone || 'unavailable'}`;
  if (error === 'ambiguous_local_time') return 'ambiguous local time during daylight-saving transition';
  if (error === 'nonexistent_local_time') return 'nonexistent local time during daylight-saving transition';
  return 'invalid timestamp';
}

function decimalTimestampOrderKey(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = String(value ?? '').trim();
  if (text.length > 1000) return null;
  const match = text.match(/^([+-]?)(\d+)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/);
  if (!match) return null;
  const numeric = Number(text);
  if (!Number.isFinite(numeric) || numeric <= 0 || /[eE]/.test(text)) return null;
  const unitPower = Math.abs(numeric) < 1e11 ? 9 : 6;
  const fraction = match[3] || '';
  const exponent = Number(match[4] || 0);
  let power = unitPower - fraction.length + exponent;
  if (power < -30 || power > 30) return null;
  let digits = BigInt(`${match[2]}${fraction}` || '0');
  if (match[1] === '-') digits = -digits;
  if (power < 0) {
    const divisor = 10n ** BigInt(-power);
    if (digits % divisor !== 0n) return null;
    digits /= divisor;
    power = 0;
  }
  if (power > 30) return null;
  return (digits * (10n ** BigInt(power))).toString();
}

function timestampOrderingEvidence(value: unknown, sourceRowOrdinal: number, row: RawRow, timestampMs?: number) {
  const originalText = typeof value === 'string' ? value : null;
  const match = originalText?.match(/(?:T|\s)\d{1,2}:\d{2}:\d{2}(?:\.(\d+))?/);
  const numericText = String(value ?? '').trim();
  const numeric = Number(numericText);
  const isScientific = typeof value === 'number' || /[eE][+-]?\d+/.test(numericText);
  const fractionalSecondDigits = match ? match[1]?.length ?? 0 : null;
  let timestampOrderKeyNs = decimalTimestampOrderKey(value);
  if (fractionalSecondDigits != null && fractionalSecondDigits <= 9 && Number.isFinite(timestampMs)) {
    const fraction = match?.[1] || '';
    timestampOrderKeyNs = (BigInt(Math.floor(timestampMs! / 1000)) * 1_000_000_000n
      + BigInt((fraction || '').padEnd(9, '0') || '0')).toString();
  }
  const precisionUnit = typeof value === 'number' ? 'numeric_value' : isScientific ? 'scientific_notation'
    : fractionalSecondDigits != null ? (fractionalSecondDigits > 3 ? 'submillisecond' : fractionalSecondDigits ? 'fractional_second' : 'second')
      : Number.isFinite(Number(numericText)) ? (Math.abs(Number(numericText)) < 1e11 ? 'second' : 'millisecond') : 'calendar_precision';
  const sequence = readBrokerSequenceEvidence(row);
  return {
    version: 1,
    originalTimestampText: originalText,
    timestampOrderKeyNs,
    timestampPrecision: {
      representation: originalText == null ? typeof value : 'text',
      fractionalSecondDigits,
      precisionUnit,
      classification: isScientific || fractionalSecondDigits == null || fractionalSecondDigits <= 3 ? 'reduced_precision' : 'preserved_submillisecond',
      normalizedPrecision: timestampOrderKeyNs ? 'ordering_key_with_source_precision' : 'millisecond_with_source_precision_metadata',
    },
    brokerSequence: sequence.brokerSequence,
    brokerSequenceScope: null,
    sequenceEvidenceSource: sequence.sequenceEvidenceSource,
    sequenceVerified: false,
    sourceRowOrdinal,
    rowOrderReliability: 'unverified',
    rowOrderEvidenceSource: null,
    importBatchId: null,
  };
}

const BROKER_SEQUENCE_HEADERS: Array<[string, string]> = [
  ['brokerExecutionSequence', 'broker_execution_sequence'],
  ['executionSequence', 'execution_sequence'],
  ['fillSequence', 'fill_sequence'],
  ['brokerSequence', 'broker_sequence'],
  ['execSequence', 'exec_sequence'],
];

function readBrokerSequenceEvidence(row: RawRow) {
  for (const [camel, snake] of BROKER_SEQUENCE_HEADERS) {
    const raw = getExecutionValue(row, [camel, snake]);
    const value = String(raw ?? '').trim();
    if (value && /^\d+$/.test(value)) {
      return { brokerSequence: value.replace(/^0+(?=\d)/, ''), sequenceEvidenceSource: snake };
    }
  }
  return { brokerSequence: null, sequenceEvidenceSource: null };
}

function getExecutionOrderingScope(execution: Execution): string {
  return JSON.stringify([
    executionScopeKey(execution),
    String(execution.symbol || '').trim().toUpperCase(),
    execution.assetClass || 'UNKNOWN',
    Number(execution.timestamp),
  ]);
}

function applyVerifiedBrokerSequences(executions: Execution[]): Execution[] {
  const groups = new Map<string, Execution[]>();
  for (const execution of executions) {
    const evidence = execution.metadata?.orderingEvidence;
    if (!evidence?.brokerSequence || !evidence?.sequenceEvidenceSource) continue;
    const key = getExecutionOrderingScope(execution);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(execution);
  }
  for (const [scope, group] of groups) {
    if (group.length < 2) continue;
    const sources = new Set(group.map((execution) => execution.metadata!.orderingEvidence!.sequenceEvidenceSource));
    const sequences = group.map((execution) => execution.metadata!.orderingEvidence!.brokerSequence);
    if (sources.size !== 1 || new Set(sequences).size !== group.length) continue;
    for (const execution of group) {
      execution.metadata!.orderingEvidence!.sequenceVerified = true;
      execution.metadata!.orderingEvidence!.brokerSequenceScope = scope;
    }
  }
  return executions;
}

function compareExecutionOrder(left: Execution, right: Execution): number {
  const leftOrderKey = left.metadata?.orderingEvidence?.timestampOrderKeyNs;
  const rightOrderKey = right.metadata?.orderingEvidence?.timestampOrderKeyNs;
  if (leftOrderKey && rightOrderKey && leftOrderKey !== rightOrderKey) {
    const leftNs = BigInt(leftOrderKey);
    const rightNs = BigInt(rightOrderKey);
    return leftNs < rightNs ? -1 : 1;
  }
  const timestampDelta = Number(left.timestamp) - Number(right.timestamp);
  if (timestampDelta) return timestampDelta;
  const leftEvidence = left.metadata?.orderingEvidence;
  const rightEvidence = right.metadata?.orderingEvidence;
  if (leftEvidence?.sequenceVerified && rightEvidence?.sequenceVerified
      && leftEvidence.sequenceEvidenceSource === rightEvidence.sequenceEvidenceSource
      && leftEvidence.brokerSequenceScope === rightEvidence.brokerSequenceScope) {
    const leftSequence = BigInt(leftEvidence.brokerSequence!);
    const rightSequence = BigInt(rightEvidence.brokerSequence!);
    return leftSequence < rightSequence ? -1 : leftSequence > rightSequence ? 1 : 0;
  }
  return 0;
}

function sameOrderingInstant(left: Execution, right: Execution): boolean {
  const leftKey = left.metadata?.orderingEvidence?.timestampOrderKeyNs;
  const rightKey = right.metadata?.orderingEvidence?.timestampOrderKeyNs;
  return leftKey && rightKey ? leftKey === rightKey : Number(left.timestamp) === Number(right.timestamp);
}

function formatNyDate(ms: number): string {
  const d = new Date(ms);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

function parseTags(v: unknown): string[] {
  if (!v && v !== 0) return [];
  if (Array.isArray(v)) return v.map(x => String(x).trim()).filter(Boolean);
  return String(v).split(/[;,|]/g).map(x => x.trim()).filter(Boolean);
}

function normalizeSide(v: unknown): 'buy' | 'sell' | undefined {
  const s = String(v ?? '').trim().toLowerCase();
  const compact = s.replace(/[\s_-]+/g, '');
  const numeric = Number(s);
  if (Number.isFinite(numeric) && numeric !== 0) return numeric < 0 ? 'sell' : 'buy';
  if (s === '-' || s === '--') return 'sell';
  if (s === '+' || s === '++') return 'buy';
  if (['s', 'sl', 'sld', 'sell', 'short', 'sellshort', 'shortsell'].includes(compact)) return 'sell';
  if (['b', 'bot', 'buy', 'long', 'buytocover', 'cover', 'buycover'].includes(compact)) return 'buy';
  if (s.includes('sell') || s.includes('short')) return 'sell';
  if (s.includes('buy') || s.includes('long')) return 'buy';
  return undefined;
}

const FUTURES_ROOTS_CLIENT_SET = new Set([
  'ES', 'MES', 'NQ', 'MNQ', 'RTY', 'M2K', 'YM', 'MYM',
  'CL', 'GC', 'SI', 'HG', 'NG', 'ZB', 'ZN', 'ZF', 'ZT',
  '6A', '6B', '6C', '6E', '6J', '6N', '6S',
]);

function isFuturesSymbolCandidate(symbol: unknown): boolean {
  const s = String(symbol ?? '').trim().toUpperCase();
  if (!s) return false;
  const compact = s.replace(/[\s._-]/g, '');
  const noVenue = compact.includes(':') ? compact.split(':').pop() || compact : compact;
  const slashParts = noVenue.split('/').filter(Boolean);
  const normalizedContract = slashParts.length > 0 ? slashParts[0] : noVenue;
  return (
    FUTURES_ROOTS_CLIENT_SET.has(normalizedContract) ||
    /^(ES|MES|NQ|MNQ|RTY|M2K|YM|MYM|CL|GC|SI|HG|NG|ZB|ZN|ZF|ZT|6A|6B|6C|6E|6J|6N|6S)[FGHJKMNQUVXZ]\d{1,2}$/.test(normalizedContract) ||
    /^[A-Z]{1,6}[FGHJKMNQUVXZ]\d{1,4}$/.test(normalizedContract) ||
    /^\d[A-Z]([FGHJKMNQUVXZ]\d{1,2})?$/.test(normalizedContract)
  );
}

export type InstrumentClassification = {
  symbol: string;
  canonicalSymbol?: string;
  indexName?: string;
  assetClass: NonNullable<AggregatedTrade['assetClass']>;
  instrumentType: 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO' | 'INDEX_CFD' | 'CASH_INDEX' | 'UNKNOWN';
  isRecognizedInstrument: boolean;
  isSupportedAssetClass: boolean;
};

export function classifyInstrument(symbol: string, hint?: string, row?: RawRow): InstrumentClassification {
  const rawSymbol = String(symbol ?? '').trim().toUpperCase();
  const indexMatch = resolveIndexInstrument(rawSymbol, hint, row);
  if (indexMatch) {
    return {
      symbol: rawSymbol,
      canonicalSymbol: indexMatch.canonicalSymbol,
      indexName: indexMatch.indexName,
      assetClass: 'STOCKS',
      instrumentType: indexMatch.instrumentType,
      isRecognizedInstrument: true,
      isSupportedAssetClass: true,
    };
  }
  const assetClass = classifyAsset(rawSymbol, hint, row) || 'UNKNOWN';
  if (assetClass !== 'UNKNOWN') {
    return {
      symbol: rawSymbol,
      assetClass,
      instrumentType: assetClass === 'CASH' ? 'FOREX' : assetClass,
      isRecognizedInstrument: true,
      isSupportedAssetClass: true,
    };
  }
  return {
    symbol: rawSymbol,
    assetClass: 'UNKNOWN',
    instrumentType: 'UNKNOWN',
    isRecognizedInstrument: false,
    isSupportedAssetClass: false,
  };
}

export function classifyAsset(symbol: string, hint?: string, row?: RawRow): AggregatedTrade['assetClass'] {
  const s = String(symbol ?? '').trim().toUpperCase();
  if (resolveIndexInstrument(s, hint, row)) {
    return 'STOCKS';
  }
  const compact = s.replace(/[\s._-]/g, '');
  const noVenue = compact.includes(':') ? compact.split(':').pop() || compact : compact;
  const isFxPair = /^[A-Z]{3}[/.]?[A-Z]{3}$/.test(noVenue);
  const slashParts = noVenue.split('/').filter(Boolean);
  const normalizedContract = !isFxPair && slashParts.length > 0 ? slashParts[0] : noVenue;
  const explicit = String(hint ?? '').toUpperCase();
  const explicitClass: AggregatedTrade['assetClass'] | undefined =
    /(STOCK|STK|EQUITY|EQUITIES)/i.test(explicit) ? 'STOCKS' :
    /(FUTURE|FUTURES|FUT|DERIVATIVE)/i.test(explicit) ? 'FUTURES' :
    /(FOREX|FX|CURRENCY)/i.test(explicit) ? 'CASH' :
    /(CRYPTO|BTC|ETH|SOL|LTC|XRP|DOGE|ADA|BNB)/i.test(explicit) ? 'CRYPTO' :
    undefined;

  const futuresRoots = new Set(['ES', 'MES', 'NQ', 'MNQ', 'RTY', 'M2K', 'YM', 'MYM', 'CL', 'GC', 'SI', 'HG', 'NG', 'ZB', 'ZN', 'ZF', 'ZT', '6A', '6B', '6C', '6E', '6J', '6N', '6S']);
  const cryptoRoots = new Set(['BTC', 'ETH', 'LTC', 'XRP', 'DOGE', 'SOL', 'ADA', 'BNB']);
  const fxCodes = new Set(['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD', 'SEK', 'NOK', 'DKK', 'HKD', 'SGD', 'CNH', 'CNY', 'MXN', 'ZAR', 'TRY', 'PLN', 'HUF', 'CZK', 'ILS', 'AED', 'SAR']);
  let symbolClass: AggregatedTrade['assetClass'] | undefined;
  if (futuresRoots.has(normalizedContract)) symbolClass = 'FUTURES';
  else if (/^(ES|MES|NQ|MNQ|RTY|M2K|YM|MYM|CL|GC|SI|HG|NG|ZB|ZN|ZF|ZT|6A|6B|6C|6E|6J|6N|6S)[FGHJKMNQUVXZ]\d{1,2}$/.test(normalizedContract)) symbolClass = 'FUTURES';
  else if (/^[A-Z]{1,6}[FGHJKMNQUVXZ]\d{1,4}$/.test(normalizedContract)) symbolClass = 'FUTURES';
  else if (/^\d[A-Z]([FGHJKMNQUVXZ]\d{1,2})?$/.test(normalizedContract)) symbolClass = 'FUTURES';
  else if (cryptoRoots.has(normalizedContract) || /^(BTC|ETH|LTC|XRP|DOGE|SOL|ADA|BNB)\b/.test(normalizedContract)) symbolClass = 'CRYPTO';
  else if (isFxPair) symbolClass = 'CASH';
  else if (/^[A-Z]{6}$/.test(normalizedContract)) {
    const base = normalizedContract.slice(0, 3);
    const quote = normalizedContract.slice(3);
    if (cryptoRoots.has(base) || cryptoRoots.has(quote)) symbolClass = 'CRYPTO';
    else if (fxCodes.has(base) && fxCodes.has(quote)) symbolClass = 'CASH';
    else if (quote === 'USD') symbolClass = 'CASH';
  } else if (/^[A-Z]{1,5}(\/[A-Z])?$/.test(s)) symbolClass = 'STOCKS';

  if (symbolClass === 'FUTURES' || symbolClass === 'CRYPTO') return symbolClass;
  if (symbolClass === 'CASH' && explicitClass && explicitClass !== 'CASH') return explicitClass;
  if (symbolClass === 'CASH') return symbolClass;
  if (explicitClass) return explicitClass;
  if (symbolClass) return symbolClass;
  return 'UNKNOWN';
}

function durationBetween(startMs?: number | null, endMs?: number | null): string | undefined {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return undefined;
  const start = Number(startMs);
  const end = Number(endMs);
  const diff = Math.max(0, end - start);
  const secs = Math.floor(diff / 1000);
  const days = Math.floor(secs / 86400);
  const hours = Math.floor((secs % 86400) / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function inferCashQuantityMultiplier(executions: Execution[]): number {
  if (!Array.isArray(executions) || executions.length === 0) return 1;
  const quantities = executions
    .map((execution) => Number(execution.qty))
    .filter((qty) => Number.isFinite(qty) && qty > 0);
  if (quantities.length === 0) return 1;

  const hasFractionalQty = quantities.some((qty) => !Number.isInteger(qty));
  if (hasFractionalQty) return 100000;

  const maxQty = Math.max(...quantities);
  return maxQty < 1000 ? 100000 : 1;
}

export type CsvExecutionValidationResult = {
  execution?: Execution;
  reasons: string[];
  assetClass: SessionAssetClass;
  warnings?: string[];
  code?: string;
};

function toSessionAssetClass(assetClass: Execution['assetClass']): SessionAssetClass {
  if (assetClass === 'STOCKS') return 'STK';
  if (assetClass === 'FUTURES') return 'FUT';
  if (assetClass === 'CASH') return 'CASH';
  return 'CRYPTO';
}

function getExecutionValue(row: RawRow, keys: string[]): unknown {
  const normalizeKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') return value;
  }
  const normalized = Object.keys(row).reduce((acc: Record<string, unknown>, key) => {
    acc[normalizeKey(key)] = row[key];
    return acc;
  }, {});
  for (const key of keys) {
    const value = normalized[normalizeKey(key)];
    if (value !== undefined && value !== null && String(value).trim() !== '') return value;
  }
  return undefined;
}

// Match the server's scope aliases and keep missing scope separate from known scope.
function readExecutionScope(row: RawRow): ExecutionScope {
  const aliases = {
    tenantId: ['tenantId', 'tenant_id', 'workspaceId', 'workspace_id'],
    userId: ['userId', 'user_id'],
    accountId: ['accountId', 'account_id', 'providerAccountId', 'provider_account_id', 'accountNumber', 'account_number', 'account', 'accountTag', 'account_tag'],
    sourceAccountRef: ['sourceAccountRef', 'source_account_ref'],
    sourceBroker: ['sourceBroker', 'source_broker', 'broker', 'brokerId', 'broker_id'],
  };
  return Object.fromEntries(Object.entries(aliases).flatMap(([field, keys]) => {
    const value = String(getExecutionValue(row, keys) ?? '').trim();
    return value ? [[field, value]] : [];
  }));
}

function executionScopeKey(execution: Execution): string {
  const scope = readExecutionScope(execution);
  const values = [scope.tenantId, scope.userId, scope.accountId, scope.sourceAccountRef, scope.sourceBroker];
  return values.some(Boolean) ? JSON.stringify(values.map((value) => value || '')) : '';
}

/** Strictly validates one CSV execution so import diagnostics and persisted data agree. */
export function validateCsvExecutionRow(row: RawRow, index: number, options?: { timeZone?: string }): CsvExecutionValidationResult {
  const input = row && typeof row === 'object' ? row : {};
  const timeZone = typeof options?.timeZone === 'string' && options.timeZone.trim() ? options.timeZone.trim() : undefined;

  // A single completed-trade summary row is one closed trade, not one execution.
  // Validate it via the summary adapter so diagnostics and canonical normalization
  // agree; the mapped executions are produced by normalizeRowsToExecutions.
  if (isCompletedTradeSummaryRow(input)) {
    const mapped = mapCompletedTradeSummaryRowToExecutions(input, timeZone);
    return { reasons: mapped.reasons, assetClass: mapped.assetClass };
  }

  const reasons: string[] = [];
  const optionType = String(getExecutionValue(input, ['optionType', 'putCall', 'type']) ?? '').trim().toLowerCase();
  const optionExpiration = getExecutionValue(input, ['expiration', 'expirationDate', 'exp']);
  const optionStrike = getExecutionValue(input, ['strike', 'strikePrice']);
  if (/^(call|put|option|options)$/.test(optionType) && optionExpiration != null && String(optionExpiration).trim()
      && optionStrike != null && String(optionStrike).trim()) {
    reasons.push('option contracts are not supported by the current trade model');
  }
  const dateRaw = getExecutionValue(input, ['date', 'datetime', 'time', 'timestamp', 'executedAt', 'filledAt', 'executionTime', 'execTime', 'fillTime', 'transactionTime']);
  const timestamp = parseDateToTimestampMs(dateRaw, timeZone);
  const timestampEvidence = timestampOrderingEvidence(dateRaw, index, input, timestamp);
  const symbol = String(getExecutionValue(input, ['symbol', 'ticker', 'instrument', 'product', 'contract', 'security']) ?? '').trim().toUpperCase();
  const assetHint = getExecutionValue(input, ['assetClass', 'market', 'marketType', 'instrumentType', 'instrument', 'securityType']);
  const instrumentClassification = classifyInstrument(symbol, typeof assetHint === 'string' ? assetHint : undefined, input);
  const assetClass = instrumentClassification.assetClass as Execution['assetClass'];
  const isIndexInstrument = instrumentClassification.instrumentType === 'INDEX_CFD' || instrumentClassification.instrumentType === 'CASH_INDEX';
  const sessionAssetClass: SessionAssetClass = isIndexInstrument ? 'CASH' : toSessionAssetClass(assetClass);
  const diagnosticAssetClass: SessionAssetClass = toSessionAssetClass(assetClass);
  const qtyInput = getExecutionValue(input, ['qty', 'quantity', 'shares', 'size', 'contracts', 'volume', 'filledQuantity', 'filledQty']);
  const qtyRaw = assetClass === 'CASH' ? parseForexQuantityValue(qtyInput) : parseOptionalNumber(qtyInput);
  const qty = Math.abs(Number(qtyRaw));
  const priceInput = getExecutionValue(input, ['price', 'fillPrice', 'executionPrice', 'avgPrice', 'averagePrice', 'tradePrice', 'entryPrice']);
  const price = parseOptionalNumber(priceInput);
  const side = normalizeSide(getExecutionValue(input, ['side', 'sideCode', 'side_code', 'orderSide', 'order_side', 'orderAction', 'b/s', 'bs', 'direction', 'action', 'type', 'position', 'positionType', 'position_type', 'positionSide', 'position_side', 'tradeSide', 'trade_side', 'buySell', 'buy_sell', 'transactionType', 'transaction_type']));

  if (!symbol) reasons.push('missing symbol');
  if (assetClass === 'UNKNOWN') reasons.push('unsupported asset class');
  if (timestamp == null) reasons.push(getTimestampFailureReason(dateRaw, timeZone));
  if (!Number.isFinite(qty) || qty <= 0) reasons.push(qtyInput == null ? 'missing quantity' : 'invalid quantity');
  if (!Number.isFinite(price) || Number(price) <= 0) reasons.push(priceInput == null ? 'missing price' : 'invalid price');
  if (!side) reasons.push(getExecutionValue(input, ['side', 'sideCode', 'side_code', 'orderSide', 'order_side', 'orderAction', 'b/s', 'bs', 'direction', 'action', 'type', 'position', 'positionType', 'position_type', 'positionSide', 'position_side', 'tradeSide', 'trade_side', 'buySell', 'buy_sell', 'transactionType', 'transaction_type']) == null ? 'missing side' : 'invalid side');
  if (timestamp != null && assetClass !== 'UNKNOWN') {
    const session = getSessionInfo(sessionAssetClass, timestamp);
    if (!session.valid && session.reason) reasons.push(session.reason);
  }
  if (reasons.length > 0 || timestamp == null || !side || !Number.isFinite(price)) {
    const timestampError = resolveSourceTimestamp(dateRaw, timeZone).error;
    const code = timestampError === 'ambiguous_local_time' ? 'AMBIGUOUS_TIMEZONE'
      : timestampError === 'invalid_timezone' ? 'INVALID_TIMEZONE'
        : timestamp == null ? 'INVALID_TIMESTAMP' : 'CSV_VALIDATION_ERROR';
    return { reasons, assetClass: diagnosticAssetClass, code };
  }

  const warnings = timestampEvidence.timestampPrecision.classification === 'reduced_precision'
    ? ["This file doesn't include precise execution times. Some advanced replay features may be limited."]
    : [];

  return {
    reasons,
    assetClass: diagnosticAssetClass,
    execution: {
      id: String(getExecutionValue(input, ['id', 'executionId', 'execId']) ?? `exec-${index}-${Date.now()}`),
      timestamp,
      symbol,
      side,
      qty,
      price: Number(price),
      commission: parseNumber(getExecutionValue(input, ['commission', 'commissions', 'comm', 'fee', 'fees', 'costs', 'commissionFees', 'commissionsFees', 'commissionsAndFees', 'commissionAndFees', 'totalFees'])),
      brokerReportedPnl: parseOptionalNumber(getExecutionValue(input, ['brokerReportedPnl', 'broker_reported_pnl', 'brokerPnl', 'reportedPnl', 'realizedPnl', 'realized_pnl', 'closedPnl', 'closed_pnl', 'pnl', 'profit', 'pl'])),
      orderId: (() => { const value = getExecutionValue(input, ['orderId', 'order_id', 'orderid', 'tradeId', 'trade_id']); return value == null ? undefined : String(value).trim() || undefined; })(),
      metadata: {
        sourceExecutionId: (() => { const value = getExecutionValue(input, ['id', 'executionId', 'execId']); return value == null ? undefined : String(value); })(),
        executionSource: 'csv_execution',
        orderingEvidence: timestampEvidence,
        ...(instrumentClassification.instrumentType !== 'UNKNOWN'
          ? { instrumentType: instrumentClassification.instrumentType }
          : {}),
        ...(instrumentClassification.canonicalSymbol
          ? { canonicalSymbol: instrumentClassification.canonicalSymbol }
          : {}),
      },
      notes: String(getExecutionValue(input, ['notes', 'note', 'comment', 'memo']) ?? '') || undefined,
      tags: parseTags(getExecutionValue(input, ['tags', 'tag', 'labels'])),
      assetClass,
      ...(instrumentClassification.instrumentType !== 'UNKNOWN'
        ? { instrumentType: instrumentClassification.instrumentType }
        : {}),
      ...(instrumentClassification.canonicalSymbol
        ? { canonicalSymbol: instrumentClassification.canonicalSymbol }
        : {}),
      ...(instrumentClassification.indexName
        ? { indexName: instrumentClassification.indexName }
        : {}),
      ...readExecutionScope(input),
    },
    warnings,
  };
}

export function normalizeRowsToExecutions(rows: RawRow[], options?: { timeZone?: string }): Execution[] {
  const sourceRows = Array.isArray(rows) ? rows : [];

  // Completed-trade summary files (one row = one closed trade with explicit
  // entry/exit time and price columns) are mapped into the canonical execution
  // representation below, then grouped by the exact same downstream pipeline.
  if (sourceRows.length > 0 && isCompletedTradeSummarySchema(sourceRows, options)) {
    return normalizeCompletedTradeSummaryToExecutions(sourceRows, options);
  }

  const executions = sourceRows
    .map((row, index) => validateCsvExecutionRow(row, index, options).execution)
    .filter((execution): execution is Execution => Boolean(execution));
  return applyVerifiedBrokerSequences(executions).sort(compareExecutionOrder);
}

// ---------------------------------------------------------------------------
// Completed-trade summary adapter.
// A file whose rows carry BOTH an entry time and an exit time column AND both an
// entry and an exit price column (plus symbol/side/quantity) is a completed-trade
// summary: one row = one closed trade. It is detected by a strong header signature
// and mapped into the canonical execution representation so it flows through the
// exact same grouping, P&L, and metadata path as execution-level imports. The
// supplied `executions` column is an execution COUNT and is preserved as metadata;
// it is never used to fabricate individual fills.
// ---------------------------------------------------------------------------

const SUMMARY_META_FIELDS = [
  'strategy',
  'setup',
  'marketSession',
  'timeframe',
  'riskAmount',
  'initialStop',
  'targetPrice',
  'mae',
  'mfe',
  'grossPnl',
  'fees',
  'sourceBroker',
  'sourceStatus',
  'shared',
  'account',
  'instrument',
  'instrumentType',
  'canonicalSymbol',
  'indexName',
  'sourceExecutionCount',
] as const;

const SUMMARY_HEADER_ALIASES: Record<string, string[]> = {
  symbol: ['symbol', 'ticker', 'instrument', 'sym', 'product', 'contract', 'sec', 'underlying', 'asset', 'code', 'stock', 'pair', 'simbolo', 'símbolo', 'activo'],
  entryTime: [
    'entrydatetime', 'entry_datetime', 'entrytime', 'entry_time', 'entry', 'entrydate', 'entry_date',
    'opentime', 'open_time', 'opendate', 'open_date', 'opened', 'open', 'openedat', 'entrytimems', 'opentimems',
    'entrytimeutc', 'entrydateutc', 'horadeentrada', 'horadeentradautc1', 'horadeentradautc01', 'horadeentradautc',
  ],
  exitTime: [
    'exitdatetime', 'exit_datetime', 'exittime', 'exit_time', 'exit', 'exitdate', 'exit_date',
    'closetime', 'close_time', 'closedate', 'close_date', 'closed', 'close', 'closedat', 'exittimems', 'closetimems',
    'exittimeutc', 'exitdateutc', 'horadecierre', 'horadecierreutc1', 'horadecierreutc01', 'horadecierreutc', 'horadesalida',
  ],
  side: ['side', 'direction', 'action', 'type', 'position', 'positiontype', 'buysell', 'transactiontype', 'orderside', 'tradeside', 'tradeaction', 'tipo', 'direccion', 'dirección'],
  quantity: ['quantity', 'qty', 'shares', 'size', 'contracts', 'volume', 'amount', 'units', 'lots', 'filledunits', 'cantidad', 'lotes', 'volumen'],
  entryPrice: ['entryprice', 'entry_price', 'openprice', 'open_price', 'avgentry', 'avgentryprice', 'avg_entry_price', 'entrypx', 'entry_px', 'openpx', 'preciodeentrada', 'precioapertura'],
  exitPrice: ['exitprice', 'exit_price', 'closeprice', 'close_price', 'avgexit', 'avgexitprice', 'avg_exit_price', 'exitpx', 'exit_px', 'closepx', 'preciodecierre', 'preciosalida'],
  orderId: ['orderid', 'tradeid', 'trade_id', 'trade', 'id', 'ref', 'refnum', 'ticket', 'transid', 'operacion', 'orden'],
  notes: ['notes', 'note', 'comment', 'memo', 'notas', 'comentario'],
  tags: ['tags', 'tag', 'labels', 'etiquetas'],
};

function normalizeHeaderKey(key: unknown): string {
  return String(key ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function collectCsvHeaders(rows: RawRow[], fields?: string[]): Set<string> {
  const headers = new Set<string>((Array.isArray(fields) ? fields : []).map(normalizeHeaderKey).filter(Boolean));
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || typeof row !== 'object') continue;
    for (const key of Object.keys(row)) {
      const nk = normalizeHeaderKey(key);
      if (nk) headers.add(nk);
    }
  }
  return headers;
}

function hasAnyHeader(headers: Set<string>, aliases: string[]): boolean {
  return aliases.some((alias) => headers.has(normalizeHeaderKey(alias)));
}

export function isCompletedTradeSummarySchema(rows: RawRow[], options?: { fields?: string[]; timeZone?: string }): boolean {
  const headers = collectCsvHeaders(rows, options?.fields);
  if (headers.size === 0) return false;
  return (
    hasAnyHeader(headers, SUMMARY_HEADER_ALIASES.symbol) &&
    hasAnyHeader(headers, SUMMARY_HEADER_ALIASES.side) &&
    hasAnyHeader(headers, SUMMARY_HEADER_ALIASES.quantity) &&
    hasAnyHeader(headers, SUMMARY_HEADER_ALIASES.entryTime) &&
    hasAnyHeader(headers, SUMMARY_HEADER_ALIASES.entryPrice)
  );
}

export function isCompletedTradeSummaryRow(row: RawRow): boolean {
  return isCompletedTradeSummarySchema([row], {});
}

function sourceHasExplicitUtcOffset(rows: RawRow[]): boolean {
  return (Array.isArray(rows) ? rows : []).some((row) => {
    const entry = getExecutionValue(row, SUMMARY_HEADER_ALIASES.entryTime);
    const exit = getExecutionValue(row, SUMMARY_HEADER_ALIASES.exitTime);
    return hasExplicitTimeZone(String(entry ?? '')) || hasExplicitTimeZone(String(exit ?? ''));
  });
}

function readTrimmedOrUndefined(row: RawRow, aliases: string[]): string | undefined {
  const value = getExecutionValue(row, aliases);
  if (value === undefined || value === null) return undefined;
  const text = String(value).trim();
  return text ? text : undefined;
}

/**
 * Session validation for completed-trade summaries. Mirrors the ACTIVE server-side
 * canonical importer's session rule (extended hours: STK 04:00-20:00 NY) so client
 * diagnostics and server normalization agree. The shared client sessionValidator
 * applies a stricter RTH rule used by market-data paths; the summary adapter must
 * agree with the server pipeline that actually constructs the trades.
 */
function getSummarySessionInfo(sessionAssetClass: SessionAssetClass, timestampMs: number): { valid: boolean; reason?: string } {
  if (!Number.isFinite(timestampMs)) return { valid: false, reason: 'Invalid timestamp' };
  const date = new Date(timestampMs);
  const day = Number(formatInTimeZone(date, 'America/New_York', 'i')) % 7;
  const hour = Number(formatInTimeZone(date, 'America/New_York', 'H'));
  const minute = Number(formatInTimeZone(date, 'America/New_York', 'm'));
  const totalMinutes = hour * 60 + minute;

  if (sessionAssetClass === 'CRYPTO') return { valid: true };
  if (sessionAssetClass === 'STK') {
    if (day === 0 || day === 6) return { valid: false, reason: 'STK weekend (Mon-Fri only)' };
    if (totalMinutes < 4 * 60) return { valid: false, reason: 'STK before 04:00 NY' };
    if (totalMinutes > 20 * 60) return { valid: false, reason: 'STK after 20:00 NY' };
    return { valid: true };
  }
  if (sessionAssetClass === 'FUT') {
    if (day === 6) return { valid: false, reason: 'FUT closed on Saturday' };
    if (day === 5 && totalMinutes >= 17 * 60) return { valid: false, reason: 'FUT closed after Friday 17:00 NY' };
    if (day === 0 && totalMinutes < 18 * 60) return { valid: false, reason: 'FUT opens Sunday 18:00 NY' };
    if (totalMinutes >= 17 * 60 && totalMinutes < 18 * 60) return { valid: false, reason: 'FUT daily maintenance break 17:00-18:00 NY' };
    return { valid: true };
  }
  if (day === 6) return { valid: false, reason: 'CASH closed on Saturday' };
  if (day === 5 && totalMinutes >= 17 * 60) return { valid: false, reason: 'CASH closed after Friday 17:00 NY' };
  if (day === 0 && totalMinutes < 17 * 60) return { valid: false, reason: 'CASH opens Sunday 17:00 NY' };
  return { valid: true };
}

export function mapCompletedTradeSummaryRowToExecutions(
  row: RawRow,
  timeZone?: string,
  index: number = 0,
): { entry: Execution | null; exit: Execution | null; reasons: string[]; assetClass: SessionAssetClass } {
  const symbol = String(getExecutionValue(row, SUMMARY_HEADER_ALIASES.symbol) ?? '').trim().toUpperCase();
  const side = normalizeSide(getExecutionValue(row, SUMMARY_HEADER_ALIASES.side));
  const qtyInput = getExecutionValue(row, SUMMARY_HEADER_ALIASES.quantity);
  const qtyRaw = parseOptionalNumber(qtyInput);
  const qty = qtyRaw == null ? Number.NaN : Math.abs(qtyRaw);

  let entryTimeRaw = getExecutionValue(row, SUMMARY_HEADER_ALIASES.entryTime);
  const entryDate = getExecutionValue(row, ['entrydate', 'entry_date', 'opendate', 'open_date', 'date', 'tradedate']);
  const entryClock = getExecutionValue(row, ['entrytime', 'entry_time', 'opentime', 'open_time', 'time', 'tradetime', 'clock']);
  if (entryDate && entryClock && (!entryTimeRaw || !/\d{1,2}:\d{2}/.test(String(entryTimeRaw)))) {
    entryTimeRaw = `${String(entryDate).trim()} ${String(entryClock).trim()}`;
  }

  let exitTimeRaw = getExecutionValue(row, SUMMARY_HEADER_ALIASES.exitTime);
  const exitDate = getExecutionValue(row, ['exitdate', 'exit_date', 'closedate', 'close_date']);
  const exitClock = getExecutionValue(row, ['exittime', 'exit_time', 'closetime', 'close_time']);
  if (exitDate && exitClock && (!exitTimeRaw || !/\d{1,2}:\d{2}/.test(String(exitTimeRaw)))) {
    exitTimeRaw = `${String(exitDate).trim()} ${String(exitClock).trim()}`;
  }

  const entryTimeMs = parseDateToTimestampMs(entryTimeRaw, timeZone);
  const exitTimeMs = exitTimeRaw ? parseDateToTimestampMs(exitTimeRaw, timeZone) : null;

  const entryPriceRaw = getExecutionValue(row, SUMMARY_HEADER_ALIASES.entryPrice);
  const exitPriceRaw = getExecutionValue(row, SUMMARY_HEADER_ALIASES.exitPrice);
  const entryPrice = parseOptionalNumber(entryPriceRaw);
  const exitPrice = parseOptionalNumber(exitPriceRaw);

  const rawTradeId = String(getExecutionValue(row, SUMMARY_HEADER_ALIASES.orderId) ?? '').trim();
  const tradeId = rawTradeId || `TRADE-${index + 1}-${symbol}-${entryTimeMs || Date.now()}`;

  const rowHeadersSet = new Set(Object.keys(row || {}).map(normalizeHeaderKey));
  const hasExitHeadersInRow = hasAnyHeader(rowHeadersSet, SUMMARY_HEADER_ALIASES.exitTime) || hasAnyHeader(rowHeadersSet, SUMMARY_HEADER_ALIASES.exitPrice);
  const rowStatus = String(readTrimmedOrUndefined(row, ['status', 'positionstatus', 'position_status', 'state', 'tradestatus', 'trade_status']) ?? '').toLowerCase();
  const isExplicitlyOpen = ['open', 'active', 'unclosed', 'holding'].includes(rowStatus);
  const isOpenPosition = isExplicitlyOpen || !hasExitHeadersInRow;

  const reasons: string[] = [];
  if (!symbol) reasons.push('missing symbol');
  if (!side) reasons.push(qtyInput == null ? 'missing side' : 'invalid side');
  if (!Number.isFinite(qty) || qty <= 0) reasons.push(qtyInput == null ? 'missing quantity' : 'invalid quantity');
  if (entryTimeMs == null) reasons.push(entryTimeRaw == null ? 'missing entry timestamp' : `invalid entry timestamp: ${getTimestampFailureReason(entryTimeRaw, timeZone)}`);
  if (entryPrice == null || entryPrice <= 0) reasons.push(entryPriceRaw == null ? 'missing entry price' : 'invalid entry price');

  if (!isOpenPosition) {
    if (exitTimeMs == null) reasons.push(exitTimeRaw == null ? 'missing exit timestamp' : `invalid exit timestamp: ${getTimestampFailureReason(exitTimeRaw, timeZone)}`);
    if (entryTimeMs != null && exitTimeMs != null && exitTimeMs < entryTimeMs) reasons.push('exit timestamp before entry timestamp');
    if (exitPrice == null || exitPrice <= 0) reasons.push(exitPriceRaw == null ? 'missing exit price' : 'invalid exit price');
  }

  const instrumentHint = readTrimmedOrUndefined(row, ['instrument', 'instrument_type', 'instrumenttype', 'securityType', 'sec_type', 'market']);
  const instrumentClassification = classifyInstrument(symbol, instrumentHint, row);
  const assetClass = instrumentClassification.assetClass as Execution['assetClass'];
  const isIndexInstrument = instrumentClassification.instrumentType === 'INDEX_CFD' || instrumentClassification.instrumentType === 'CASH_INDEX';
  const sessionAssetClass: SessionAssetClass = isIndexInstrument ? 'CASH' : toSessionAssetClass(assetClass);
  const diagnosticAssetClass: SessionAssetClass = toSessionAssetClass(assetClass);
  if (reasons.length === 0 && assetClass !== 'UNKNOWN') {
    const timestampsToCheck = isOpenPosition ? [entryTimeMs] : [entryTimeMs, exitTimeMs];
    for (const ts of timestampsToCheck) {
      if (ts == null) continue;
      const session = getSummarySessionInfo(sessionAssetClass, ts);
      if (!session.valid && session.reason) reasons.push(session.reason);
    }
  }
  if (reasons.length > 0 || entryTimeMs == null || (!isOpenPosition && (exitTimeMs == null || exitPrice == null)) || entryPrice == null || !side || !symbol) {
    return { entry: null, exit: null, reasons, assetClass: diagnosticAssetClass };
  }

  const commissions = parseOptionalNumber(getExecutionValue(row, ['commissions', 'commission', 'comm', 'comisiondelbroker', 'comision']));
  const fees = parseOptionalNumber(getExecutionValue(row, ['fees', 'fee', 'costs', 'commissionfees', 'swaps', 'swap']));
  const netPnl = parseOptionalNumber(getExecutionValue(row, ['net_pnl', 'netpnl', 'netpl', 'netPnl', 'pnl', 'realizedpnl', 'realizedPnl', 'closedpnl', 'brokerpnl', 'profit', 'pl', 'neto', 'beneficioneto']));
  const grossPnl = parseOptionalNumber(getExecutionValue(row, ['gross_pnl', 'grosspnl', 'grosspl', 'grossPnl', 'bruto']));
  const tags = parseTags(getExecutionValue(row, SUMMARY_HEADER_ALIASES.tags));
  const notes = String(getExecutionValue(row, SUMMARY_HEADER_ALIASES.notes) ?? '').trim() || undefined;

  const meta: Partial<Execution> = {
    strategy: readTrimmedOrUndefined(row, ['strategy']),
    setup: readTrimmedOrUndefined(row, ['setup']),
    marketSession: readTrimmedOrUndefined(row, ['market_session', 'marketsession', 'session']),
    timeframe: readTrimmedOrUndefined(row, ['timeframe']),
    riskAmount: parseOptionalNumber(getExecutionValue(row, ['risk_amount', 'riskamount'])),
    initialStop: parseOptionalNumber(getExecutionValue(row, ['initial_stop', 'initialstop', 'stop_price'])),
    targetPrice: parseOptionalNumber(getExecutionValue(row, ['target_price', 'targetprice', 'target'])),
    mae: parseOptionalNumber(getExecutionValue(row, ['mae'])),
    mfe: parseOptionalNumber(getExecutionValue(row, ['mfe'])),
    grossPnl,
    fees,
    sourceBroker: readTrimmedOrUndefined(row, ['broker']),
    sourceStatus: readTrimmedOrUndefined(row, ['status']),
    shared: readTrimmedOrUndefined(row, ['shared']),
    account: readTrimmedOrUndefined(row, ['account']),
    instrument: readTrimmedOrUndefined(row, ['instrument', 'instrument_type', 'instrumenttype']),
    instrumentType: isIndexInstrument ? instrumentClassification.instrumentType : undefined,
    canonicalSymbol: instrumentClassification.canonicalSymbol,
    indexName: instrumentClassification.indexName,
    sourceExecutionCount: parseOptionalNumber(getExecutionValue(row, ['executions'])),
  };
  const cleanMeta: Record<string, any> = {};
  for (const key of SUMMARY_META_FIELDS) {
    // Fees are a cost component, not descriptive metadata to copy to both legs.
    // They are assigned once to the synthetic closing execution below.
    if (key === 'fees') continue;
    const value = meta[key];
    if (value !== undefined && value !== null && value !== '') cleanMeta[key] = value;
  }

  const openingSide = side;
  const closingSide: 'buy' | 'sell' = openingSide === 'buy' ? 'sell' : 'buy';
  const totalCommission = Number(commissions || 0);

  const entry: Execution = {
    id: `${tradeId}-entry`,
    timestamp: entryTimeMs,
    symbol,
    side: openingSide,
    qty,
    price: Number(entryPrice),
    commission: 0,
    fees: 0,
    orderId: tradeId,
    notes,
    tags,
    assetClass,
    // Honest labeling: canonical proxy executions derived from a completed-trade
    // summary row, NOT original broker fills.
    executionSource: 'completed_trade_summary',
    isSyntheticExecution: true,
    ...readExecutionScope(row),
    ...cleanMeta,
  };

  const exit: Execution | null = isOpenPosition ? null : {
    id: `${tradeId}-exit`,
    timestamp: exitTimeMs!,
    symbol,
    side: closingSide,
    qty,
    price: Number(exitPrice),
    commission: totalCommission,
    fees: Number(fees || 0),
    brokerReportedPnl: netPnl,
    orderId: tradeId,
    tags,
    assetClass,
    // Honest labeling: see entry execution.
    executionSource: 'completed_trade_summary',
    isSyntheticExecution: true,
    ...readExecutionScope(row),
    ...cleanMeta,
  };

  return { entry, exit, reasons, assetClass: diagnosticAssetClass };
}

export function normalizeCompletedTradeSummaryToExecutions(rows: RawRow[], options?: { timeZone?: string }): Execution[] {
  const timeZone = typeof options?.timeZone === 'string' && options.timeZone.trim() ? options.timeZone.trim() : undefined;
  const sourceRows = Array.isArray(rows) ? rows : [];

  if (!sourceHasExplicitUtcOffset(sourceRows)) {
    const zoneLabel = timeZone || 'unconfirmed; naive timestamps will be rejected';
    console.warn('[universalImporter] Completed-trade summary has timestamps without explicit offsets', { timeZone: zoneLabel, rowCount: sourceRows.length });
  }

  const executions: Execution[] = [];
  for (let index = 0; index < sourceRows.length; index += 1) {
    const row = sourceRows[index];
    if (!row || typeof row !== 'object') continue;
    const mapped = mapCompletedTradeSummaryRowToExecutions(row, timeZone, index);
    if (mapped.entry) {
      mapped.entry.metadata = {
        ...(mapped.entry.metadata || {}),
        orderingEvidence: timestampOrderingEvidence(getExecutionValue(row, SUMMARY_HEADER_ALIASES.entryTime), index, row, mapped.entry.timestamp),
      };
      executions.push(mapped.entry);
      if (mapped.exit) {
        mapped.exit.metadata = {
          ...(mapped.exit.metadata || {}),
          orderingEvidence: timestampOrderingEvidence(getExecutionValue(row, SUMMARY_HEADER_ALIASES.exitTime), index, row, mapped.exit.timestamp),
        };
        executions.push(mapped.exit);
      }
    }
  }
  return executions.sort(compareExecutionOrder);
}

export function groupExecutionsToTrades(execs: Execution[]): AggregatedTrade[] {
  if (!Array.isArray(execs) || execs.length === 0) return [];
  const sortedExecs = execs.slice().sort(compareExecutionOrder);

  let tradeCounter = 1;
  const generateTradeId = () => `T${tradeCounter++}`;

  const splitExecutionsByPosition = (input: Execution[]): Array<{ list: Execution[]; closed: boolean; isReversal: boolean; reversalFromIndex?: number }> => {
    if (!Array.isArray(input) || input.length === 0) return [];

    const sorted = input.slice().sort(compareExecutionOrder);
    const segments: Array<{ list: Execution[]; closed: boolean; isReversal: boolean; reversalFromIndex?: number }> = [];
    let current: Execution[] = [];
    let net = 0;
    let pieceIndex = 0;
    let currentIsReversal = false;
    let currentReversalFromIndex: number | undefined;
    let pendingReversalFromIndex: number | undefined;

    const makePiece = (e: Execution, qty: number): Execution => {
      const ratio = e.qty > 0 ? qty / e.qty : 0;
      const commission = Number.isFinite(Number(e.commission)) ? Number(e.commission) * ratio : 0;
      const brokerReportedPnl = (typeof e.brokerReportedPnl === 'number' && Number.isFinite(e.brokerReportedPnl))
        ? e.brokerReportedPnl * ratio
        : undefined;
      pieceIndex += 1;
      return {
        ...e,
        id: `${String(e.id)}#${pieceIndex}`,
        qty,
        commission,
        brokerReportedPnl,
      };
    };

    const pushClosedSegment = () => {
      if (!current.length) return;
      segments.push({ list: current.slice(), closed: true, isReversal: currentIsReversal, reversalFromIndex: currentReversalFromIndex });
      current = [];
      net = 0;
      currentIsReversal = false;
      currentReversalFromIndex = undefined;
    };

    for (const e of sorted) {
      let remaining = Number(e.qty) || 0;
      if (remaining <= 0) continue;
      const incomingSign = e.side === 'buy' ? 1 : -1;

      while (remaining > 0) {
        if (net === 0) {
          if (!current.length && pendingReversalFromIndex != null) {
            currentIsReversal = true;
            currentReversalFromIndex = pendingReversalFromIndex;
            pendingReversalFromIndex = undefined;
          }
          const openPiece = makePiece(e, remaining);
          current.push(openPiece);
          net += incomingSign * remaining;
          remaining = 0;
          continue;
        }

        const positionSign = net > 0 ? 1 : -1;
        if (incomingSign === positionSign) {
          const addPiece = makePiece(e, remaining);
          current.push(addPiece);
          net += incomingSign * remaining;
          remaining = 0;
          continue;
        }

        const closeQty = Math.min(remaining, Math.abs(net));
        const closePiece = makePiece(e, closeQty);
        current.push(closePiece);
        const tolerance = Number.EPSILON * 16 * Math.max(Math.abs(net), remaining);
        net += incomingSign * closeQty;
        remaining -= closeQty;
        if (Math.abs(net) <= tolerance) net = 0;
        if (remaining <= tolerance) remaining = 0;

        if (net === 0) {
          const closedIndex = segments.length;
          pushClosedSegment();
          if (remaining > 0) {
            pendingReversalFromIndex = closedIndex;
          }
        }
      }
    }

    if (current.length) {
      segments.push({ list: current.slice(), closed: net === 0, isReversal: currentIsReversal, reversalFromIndex: currentReversalFromIndex });
    }

    return segments;
  };

  const trades: AggregatedTrade[] = [];

  // helper to aggregate a list of executions into one trade record
  const aggregateList = (list: Execution[], closed: boolean, isReversal: boolean, reversalOf?: string) => {
    if (!list.length) return;
    const first = list[0];
    const last = list[list.length - 1];
    const openingSide = first.side === 'buy' ? 'Long' : 'Short';
    const sourceOrderIds = Array.from(new Set(list.map(e => String(e.orderId ?? '').trim()).filter(Boolean)));
    const tradeId = generateTradeId();

    let net = 0;
    let maxSize = 0;
    let buyQty = 0, buyValue = 0;
    let sellQty = 0, sellValue = 0;
    let totalCommission = 0;
    let totalFees = 0;
    let brokerReportedPnl: number | undefined;
    const tagSet = new Set<string>();
    const notes: string[] = [];

    for (const e of list) {
      const signed = e.side === 'buy' ? e.qty : -e.qty;
      net += signed;
      maxSize = Math.max(maxSize, Math.abs(net));
      if (e.side === 'buy') { buyQty += e.qty; buyValue += e.qty * e.price; }
      else { sellQty += e.qty; sellValue += e.qty * e.price; }
      totalCommission += Number(e.commission ?? 0);
      totalFees += Number(e.fees ?? 0);
      if (typeof e.brokerReportedPnl === 'number' && Number.isFinite(e.brokerReportedPnl)) {
        brokerReportedPnl = e.brokerReportedPnl;
      }
      for (const t of e.tags || []) tagSet.add(t);
      if (e.notes) notes.push(e.notes);
    }

    const entryPrice = openingSide === 'Long' ? (buyQty ? buyValue / buyQty : first.price) : (sellQty ? sellValue / sellQty : first.price);
    const exitPrice = openingSide === 'Long' ? (sellQty ? sellValue / sellQty : undefined) : (buyQty ? buyValue / buyQty : undefined);

    // FIFO lot matching for realized gross PnL
    const fifo = calculateFifoGrossPnl(
      list.map((execution) => ({
        side: execution.side as 'buy' | 'sell',
        qty: execution.qty,
        price: execution.price,
      }))
    );
    const grossPnl = closed ? fifo.grossPnl : 0;
    // apply contract multiplier for futures, and infer lot-vs-unit scaling for CASH/forex
    const isCash = first.assetClass === 'CASH';
    const baseMultiplier = getContractMultiplier(first.symbol, first.assetClass, (first as any)?.instrument);
    const cashQtyMultiplier = isCash ? inferCashQuantityMultiplier(list) : 1;
    const multiplier = isCash ? Math.max(baseMultiplier, cashQtyMultiplier) : baseMultiplier;
    const appliesMultiplier = first.assetClass === 'FUTURES' || first.assetClass === 'CASH';
    const adjustedGrossPnl = appliesMultiplier ? grossPnl * multiplier : grossPnl;
    const rawCalculatedPnl = closed ? (adjustedGrossPnl - totalCommission - totalFees) : null;
    const normalizedBrokerPnl = closed && typeof brokerReportedPnl === 'number'
      ? brokerReportedPnl
      : null;
    const preferBrokerPnl =
      closed &&
      normalizedBrokerPnl != null &&
      (first.executionSource === 'completed_trade_summary' ||
        first.instrumentType === 'INDEX_CFD' ||
        first.instrumentType === 'CASH_INDEX' ||
        Boolean(resolveIndexInstrument(first.symbol, first.instrumentType || first.instrument, first)));
    const calculatedPnl = preferBrokerPnl ? normalizedBrokerPnl : rawCalculatedPnl;
    const pnlDifference = normalizedBrokerPnl != null && calculatedPnl != null
      ? (preferBrokerPnl ? 0 : (normalizedBrokerPnl - calculatedPnl))
      : null;

    const validExecMs = list
      .map((exec) => normalizeTimestampToMs(exec.timestamp))
      .filter((ts): ts is number => Number.isFinite(ts as number) && (ts as number) > 0);

    const openTimestampMs = validExecMs.length > 0 ? Math.min(...validExecMs) : (normalizeTimestampToMs(first.timestamp) ?? Date.now());
    const closeTimestampMs = closed
      ? (validExecMs.length > 0 ? Math.max(...validExecMs) : normalizeTimestampToMs(last.timestamp))
      : null;
    trades.push({
      id: hashStringToPositiveInt(`${first.id}|${first.symbol}|${first.timestamp}|${closed ? last.timestamp : 'open'}${executionScopeKey(first) ? `|${executionScopeKey(first)}` : ''}`),
      tradeId,
      sourceOrderIds,
      isReversal,
      reversalOf,
      symbol: first.symbol,
      side: openingSide,
      openDate: formatNyDate(openTimestampMs),
      closeDate: closeTimestampMs != null ? formatNyDate(closeTimestampMs) : null,
      openTimestampMs,
      closeTimestampMs,
      duration: durationBetween(openTimestampMs, closeTimestampMs),
      entryPrice: entryPrice || undefined,
      exitPrice: closed ? exitPrice : undefined,
      volume: list.reduce((s, x) => s + x.qty, 0),
      quantity: maxSize,
      status: (closed ? 'closed' : 'open') as 'closed' | 'open',
      pnl: calculatedPnl === null ? null : Number(calculatedPnl),
      calculatedPnl: calculatedPnl === null ? null : Number(calculatedPnl),
      brokerReportedPnl: normalizedBrokerPnl,
      pnlDifference: pnlDifference === null ? null : Number(pnlDifference),
      commission: totalCommission,
      fees: totalFees,
      commissionAndFees: totalCommission + totalFees,
      executions: list,
      notes: notes.join(' | '),
      tags: Array.from(tagSet),
      assetClass: first.assetClass || 'UNKNOWN',
      ...(first.instrumentType ? { instrumentType: first.instrumentType } : {}),
      ...(first.canonicalSymbol ? { canonicalSymbol: first.canonicalSymbol } : {}),
      ...(first.indexName ? { indexName: first.indexName } : {}),
      pnlCurrency: first.assetClass === 'CASH' ? getForexQuoteCurrency(first.symbol) : undefined,
      ...readExecutionScope(first),
      // Hoist optional completed-trade-summary metadata verbatim (never recalculated).
      ...Object.fromEntries(
        SUMMARY_META_FIELDS
          .filter((key) => key !== 'fees' && first[key] !== undefined && first[key] !== null && first[key] !== '')
          .map((key) => [key, first[key]])
      ),
    });
  };

  // Order IDs identify orders, not positions. Each scoped instrument runs from
  // flat to flat; only completed-trade summaries retain their explicit row boundary.
  const bySymbol = new Map<string, Execution[]>();
  for (const e of sortedExecs) {
    const key = JSON.stringify([
      executionScopeKey(e), String(e.symbol).trim().toUpperCase(), e.assetClass || 'UNKNOWN',
      e.executionSource === 'completed_trade_summary' ? String(e.orderId ?? e.id) : null,
    ]);
    if (!bySymbol.has(key)) bySymbol.set(key, []);
    bySymbol.get(key)!.push(e);
  }

  for (const [, arr] of bySymbol.entries()) {
    const builtInOrder: string[] = [];
    const pendingLinks: Array<{ childTradeId: string; parentIndex: number }> = [];

    for (const seg of splitExecutionsByPosition(arr)) {
      aggregateList(seg.list, seg.closed, seg.isReversal, undefined);
      const justBuilt = trades[trades.length - 1];
      builtInOrder.push(justBuilt.tradeId);
      if (seg.isReversal && seg.reversalFromIndex != null) {
        pendingLinks.push({ childTradeId: justBuilt.tradeId, parentIndex: seg.reversalFromIndex });
      }
    }

    for (const link of pendingLinks) {
      const parentTradeId = builtInOrder[link.parentIndex];
      if (!parentTradeId) continue;
      const child = trades.find(t => t.tradeId === link.childTradeId);
      if (child) child.reversalOf = parentTradeId;
    }
  }

  return trades.sort((a, b) => a.openTimestampMs - b.openTimestampMs);
}

function stableOutcomeNumber(value: unknown): string | null {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(8) : null;
}

function executionOutcomeSignature(executions: Execution[]): string {
  return JSON.stringify(groupExecutionsToTrades(executions).map((trade) => ({
    symbol: trade.symbol,
    side: trade.side,
    status: (trade as any).status,
    openTimestampMs: trade.openTimestampMs ?? null,
    closeTimestampMs: trade.closeTimestampMs ?? null,
    quantity: stableOutcomeNumber(trade.quantity),
    volume: stableOutcomeNumber(trade.volume),
    entryPrice: stableOutcomeNumber(trade.entryPrice),
    exitPrice: stableOutcomeNumber(trade.exitPrice),
    calculatedPnl: stableOutcomeNumber(trade.calculatedPnl),
    brokerReportedPnl: stableOutcomeNumber(trade.brokerReportedPnl),
    commission: stableOutcomeNumber(trade.commission),
    fees: stableOutcomeNumber(trade.fees),
    executions: (trade.executions || []).map((execution) => ({
      id: String(execution.sourceExecutionId || execution.id || '').replace(/#\d+$/, ''),
      side: execution.side,
      quantity: stableOutcomeNumber(execution.qty),
      price: stableOutcomeNumber(execution.price),
      commission: stableOutcomeNumber(execution.commission),
      fees: stableOutcomeNumber((execution as any).fees),
      brokerReportedPnl: stableOutcomeNumber(execution.brokerReportedPnl),
    })).sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right))),
  })).sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right))));
}

function tiedOrdersProduceEquivalentOutcomes(executions: Execution[], start: number, end: number) {
  const tied = executions.slice(start, end);
  if (tied.length > 6) return { equivalent: false, bounded: false };
  const baseline = executionOutcomeSignature(executions);
  const used = new Array(tied.length).fill(false);
  const ordering: Execution[] = [];
  let equivalent = true;
  const visit = () => {
    if (!equivalent) return;
    if (ordering.length === tied.length) {
      const candidate = [...executions.slice(0, start), ...ordering, ...executions.slice(end)];
      if (executionOutcomeSignature(candidate) !== baseline) equivalent = false;
      return;
    }
    for (let index = 0; index < tied.length; index += 1) {
      if (used[index]) continue;
      used[index] = true;
      ordering.push(tied[index]);
      visit();
      ordering.pop();
      used[index] = false;
      if (!equivalent) return;
    }
  };
  visit();
  return { equivalent, bounded: true };
}

export function findAmbiguousExecutionOrdering(rows: RawRow[], options?: { timeZone?: string }) {
  const groups = new Map<string, Execution[]>();
  for (const execution of normalizeRowsToExecutions(rows, options)) {
    if (execution.executionSource === 'completed_trade_summary') continue;
    const key = JSON.stringify([
      executionScopeKey(execution), String(execution.symbol).trim().toUpperCase(), execution.assetClass || 'UNKNOWN',
    ]);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(execution);
  }

  const ambiguous: Array<Record<string, unknown>> = [];
  for (const scopedExecutions of groups.values()) {
    scopedExecutions.sort(compareExecutionOrder);
    for (let index = 0; index < scopedExecutions.length;) {
      let end = index + 1;
      while (end < scopedExecutions.length && sameOrderingInstant(scopedExecutions[end], scopedExecutions[index])) end += 1;
      const tied = scopedExecutions.slice(index, end);
      const hasBothSides = tied.some((execution) => execution.side === 'buy') && tied.some((execution) => execution.side === 'sell');
      const evidence = tied.map((execution) => execution.metadata?.orderingEvidence);
      const hasVerifiedSequence = tied.length > 1
        && evidence.every((item) => item?.sequenceVerified && item.brokerSequenceScope === evidence[0]?.brokerSequenceScope);
      const outcomeCheck = hasBothSides && !hasVerifiedSequence
        ? tiedOrdersProduceEquivalentOutcomes(scopedExecutions, index, end)
        : { equivalent: true, bounded: true };
      if (hasBothSides && !hasVerifiedSequence && !outcomeCheck.equivalent) {
        const rowOrdinals = [...new Set(tied.flatMap((execution) => {
          const ordinal = Number(execution.metadata?.orderingEvidence?.sourceRowOrdinal);
          return Number.isInteger(ordinal) && ordinal >= 0 ? [ordinal + 1] : [];
        }))].sort((left, right) => left - right);
        ambiguous.push({
          code: 'AMBIGUOUS_EXECUTION_ORDER',
          severity: 'requires_review',
          symbol: tied[0].symbol,
          assetClass: tied[0].assetClass,
          timestamp: tied[0].timestamp,
          rowOrdinals,
          analysis: outcomeCheck.bounded ? 'all_distinct_orders_compared' : 'bounded_conservative_review',
          explanation: outcomeCheck.bounded
            ? 'The tied executions produce different position boundaries, quantities, or financial allocations depending on their order.'
            : 'This collision group is too large to enumerate safely, and contains opposing sides whose outcomes cannot be proven equivalent within the ordering-analysis limit.',
          suggestedAction: 'Provide a verified broker sequence column, or correct the source timestamps using the broker export.',
          reasons: [outcomeCheck.bounded
            ? 'Possible execution orders produce materially different trade boundaries or financial allocations.'
            : 'Large mixed-side timestamp collisions require verified broker sequence evidence or corrected timestamps.'],
        });
      }
      index = end;
    }
  }
  return ambiguous;
}

export function parseRowsToTrades(rows: RawRow[], options?: { timeZone?: string }): AggregatedTrade[] {
  const sourceRows = Array.isArray(rows) ? rows : [];
  const isSummary = sourceRows.length > 0 && isCompletedTradeSummarySchema(sourceRows, options);
  const execs = normalizeRowsToExecutions(sourceRows, options);
  const trades = groupExecutionsToTrades(execs);

  if (isSummary) {
    // Completed-trade summaries supply the authoritative net P&L per row. Preserve
    // it verbatim as the canonical trade P&L (the generic execution pipeline's own
    // derived gross remains available in metadata), so supplied values are never
    // silently recalculated or inverted for short trades.
    for (const t of trades) {
      if (typeof t.brokerReportedPnl === 'number' && Number.isFinite(t.brokerReportedPnl)) {
        t.calculatedPnl = t.brokerReportedPnl;
        t.pnl = t.brokerReportedPnl;
        t.pnlDifference = 0;
      }
    }
  }

  return trades;
}
